import Cookies from "js-cookie";

import { INTERNAL_SUPPORT_ACCESS_FLAG } from "@/config/releaseFlags";
import { GenericAPIResponse } from "@/interfaces/IAuth";
import { BACKEND, axiosInstance, parseResponse } from "@/services";
import {
  clearSupportTenant,
  setSupportMode,
  supportCookieOptions,
} from "@/utils/support-access";

type SupportLoginPayload = {
  userNameOrEmail: string;
  password: string;
  shouldRemember?: boolean;
  deviceInfo?: Record<string, any>;
};

type SupportLoginResponse = GenericAPIResponse<any> & {
  token?: string;
};

export type SupportTenant = {
  _id: string;
  name: string;
  userId?: string;
  user?: Array<{
    _id: string;
    companyName?: string;
    email?: string;
    approved?: boolean;
  }>;
};

const FEATURE_FLAGS_API_URL = BACKEND?.endsWith("/")
  ? `${BACKEND}api/feature-flags/v1`
  : `${BACKEND}/api/feature-flags/v1`;

export const isInternalSupportAccessEnabled = async (token?: string) => {
  const resolvedToken = token || Cookies.get("token");

  if (!resolvedToken) {
    return false;
  }

  try {
    const response = await axiosInstance.get(
      `${FEATURE_FLAGS_API_URL}/bootstrap`,
      {
        params: { app: "customer-web" },
        headers: {
          Authorization: `Bearer ${resolvedToken}`,
          "x-wisterias-app": "customer-web",
        },
      }
    );

    return Boolean(
      response.data?.success &&
      response.data?.flags?.[INTERNAL_SUPPORT_ACCESS_FLAG]?.enabled
    );
  } catch {
    return false;
  }
};

export const loginSupport = async (
  data: SupportLoginPayload
): Promise<SupportLoginResponse> => {
  try {
    const response = await axiosInstance.post(`/admin/login`, data);
    const parsedResponse = parseResponse(response);

    if (parsedResponse?.success && parsedResponse?.token) {
      const enabled = await isInternalSupportAccessEnabled(
        parsedResponse.token
      );

      if (!enabled) {
        return {
          success: false,
          message: "Internal support access is not enabled.",
          data: null,
        };
      }

      Cookies.set("token", parsedResponse.token, supportCookieOptions());
      setSupportMode();
      clearSupportTenant();
    }

    return parsedResponse;
  } catch (error: any) {
    if (error?.response) {
      return parseResponse(error.response);
    }

    return {
      success: false,
      message:
        error?.message ||
        "Support login request failed before the server responded",
      data: null,
    };
  }
};

export const getSupportTenants = async (): Promise<
  GenericAPIResponse<{ tenants: SupportTenant[] }> & {
    tenants?: SupportTenant[];
  }
> => {
  try {
    const response = await axiosInstance.get(`/admin/support/tenants`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};
